import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { TutorInfoFields } from "@/components/auth/TutorInfoFields";
import { ContactFields } from "@/components/auth/ContactFields";
import { AuthLogo } from "@/components/auth/AuthLogo";
import { ReferralHandler } from '@/components/auth/ReferralHandler';
import { useReferralTracking } from '@/hooks/useReferralTracking';
import { Checkbox } from "@/components/ui/checkbox";
import { TermsDialog } from "@/components/auth/TermsDialog";

const Register: FC = () => {
  const navigate = useNavigate();
  const { updateReferralStatus } = useReferralTracking();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [tutorName, setTutorName] = useState("");
  const [studentName, setStudentName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [password, setPassword] = useState("");
  const [countryCode, setCountryCode] = useState("AR");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    // Detectar el país del usuario
    fetch('https://ipapi.co/json/')
      .then(response => response.json())
      .then(data => {
        console.log('IP Geolocation:', data);
        setCountryCode(data.country_code);
      })
      .catch(error => {
        console.error('Error getting country:', error);
      });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!acceptedTerms) {
      toast({
        title: "Error",
        description: "Debes aceptar las Políticas y Condiciones para continuar",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            tutor_name: tutorName,
            whatsapp,
            student_name: studentName,
            terms_accepted_at: new Date().toISOString(),
          },
        },
      });

      if (signUpError) throw signUpError;

      if (data?.user) {
        await updateReferralStatus(data.user.id, 'trial');
        
        const { error: profileError } = await supabase
          .from("profiles")
          .update({
            tutor_name: tutorName,
            whatsapp,
            student_name: studentName,
            terms_accepted_at: new Date().toISOString(),
          })
          .eq("id", data.user.id);

        if (profileError) throw profileError;

        toast({
          title: "¡Registro exitoso!",
          description: (
            <div className="space-y-2">
              <p>Hemos enviado un correo de verificación a <span className="font-medium">{email}</span></p>
              <p>👉 Por favor, revisa tu bandeja de entrada y haz clic en el enlace de verificación.</p>
              <p className="text-sm text-muted-foreground">No podrás acceder a la plataforma hasta que confirmes tu correo electrónico.</p>
            </div>
          ),
          duration: 10000, // 10 segundos para dar tiempo a leer
        });

        navigate("/login");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f6fa] p-3 md:p-6">
      <ReferralHandler />
      <div className="w-full max-w-6xl flex flex-col md:flex-row md:gap-6 items-start">
        {/* Columna izquierda - Copy y beneficios */}
        <div className="w-full md:w-1/2 md:sticky md:top-6 space-y-5">
          <div className="flex flex-col items-center space-y-4 md:items-start">
            <div className="w-full flex justify-center md:justify-start">
              <AuthLogo className="w-auto h-20" />
            </div>

            <div className="text-center md:text-left space-y-2">
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                ¡Te damos la bienvenida!
              </h1>
              <p className="text-lg text-gray-600">
                La plataforma educativa que enseña finanzas a niños y niñas de 8 a 12 años
                de una manera divertida y efectiva
              </p>
            </div>

            <div className="bg-primary/5 rounded-lg p-5 space-y-3">
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
                  <span className="text-2xl">🎁</span> Plan Free por 7 días
                </h3>
                <p className="text-gray-600">
                  Regístrate hoy y obtén <span className="font-medium">acceso gratuito por 7 días</span> para 
                  explorar nuestra plataforma sin compromiso.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-3">
              <div className="flex items-start gap-3 bg-white p-4 rounded-lg border border-primary/10">
                <span className="text-primary text-2xl">🎮</span>
                <div>
                  <p className="font-medium text-gray-900">Juegos Educativos</p>
                  <p className="text-sm text-gray-600">Aprende finanzas jugando con actividades interactivas diseñadas para niños</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white p-4 rounded-lg border border-primary/10">
                <span className="text-primary text-2xl">📚</span>
                <div>
                  <p className="font-medium text-gray-900">Contenido Adaptado</p>
                  <p className="text-sm text-gray-600">Material educativo especialmente diseñado para niños de 8 a 12 años</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Columna derecha - Formulario */}
        <div className="w-full md:w-1/2 md:mt-0 mt-5">
          <Card className="w-full shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-semibold">Regístrate para comenzar tu prueba gratuita</CardTitle>
            </CardHeader>

            <form onSubmit={handleSubmit} className="space-y-6 px-5 pb-5">
              <TutorInfoFields
                tutorName={tutorName}
                setTutorName={setTutorName}
                studentName={studentName}
                setStudentName={setStudentName}
              />

              <ContactFields
                email={email}
                setEmail={setEmail}
                whatsapp={whatsapp}
                setWhatsapp={setWhatsapp}
                password={password}
                setPassword={setPassword}
                countryCode={countryCode}
              />

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="terms"
                    checked={acceptedTerms}
                    onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
                  />
                  <label
                    htmlFor="terms"
                    className="text-xs text-gray-600 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Acepto las{" "}
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => setShowTerms(true)}
                    >
                      Políticas y Condiciones
                    </button>
                  </label>
                </div>
                <span className="text-xs text-gray-600">
                  ¿Tienes cuenta?{" "}
                  <a href="/login" className="text-primary hover:underline">
                    Inicia sesión
                  </a>
                </span>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-primary via-secondary to-accent hover:opacity-90 transition-opacity"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⚡</span>
                    Creando tu cuenta...
                  </span>
                ) : (
                  "Comenzar prueba gratuita"
                )}
              </Button>
            </form>
          </Card>
        </div>
      </div>

      <TermsDialog open={showTerms} onOpenChange={setShowTerms} />
    </div>
  );
};

export default Register;